import * as React from 'react';
import { CSSProperties, memo } from 'react';
import { Badge, Col, Flex, Grid, Row, Space } from 'antd';
import clsx from 'clsx';
import { NoContentArticle, Tag } from '../admin-knowledge-base.type';
import Controls from './controls';
import { getMenuItems } from '../admin-knowledge-base.utils';
import { DragOutlined, LeftCircleOutlined, PlusCircleOutlined } from '@ant-design/icons';
import Button from '/react4xp/mixins/ui/button/button';
import {
    closestCenter,
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    MouseSensor,
    TouchSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    rectSortingStrategy,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import TagIcon from '/react4xp/parts/knowledge-base-common/components/tag-icon';
import { validatePrivileges } from 'mixins/helpers';

interface CategoryViewProps {
    tags: Tag[];
    selectTag: (tag: Tag) => void;
    goBack: () => void;
    showUpdateForm: (tag: Tag) => void;
    showDeleteForm: (tag: Tag) => void;
    showCreateForm: () => void;
    handleReorder: (event: DragEndEvent) => void;
    articlesByTag: Map<string, NoContentArticle[]>;
    roles: string[];
    privileges: string[];
}

interface SortableTagProps {
    tag: Tag;
    articlesCount: number;
    selectTag: (tag: Tag) => void;
    showUpdateForm: (tag: Tag) => void;
    showDeleteForm: (tag: Tag) => void;
    roles: string[];
    privileges: string[];
}

const SortableTag = ({
    selectTag,
    showDeleteForm,
    showUpdateForm,
    tag,
    articlesCount,
    roles,
    privileges,
}: SortableTagProps) => {
    const { attributes, listeners, setNodeRef, transform, transition, setActivatorNodeRef } =
        useSortable({ id: tag.id });

    const style: CSSProperties = {
        transform: CSS.Transform.toString(transform),
        cursor: 'move',
        transition,
    };

    const breakpoint = Grid.useBreakpoint();

    return (
        <Col
            xs={24}
            sm={12}
            md={24}
            lg={12}
            xl={8}
            ref={setNodeRef}
            style={style}
            {...attributes}
        >
            <Badge.Ribbon
                text={articlesCount}
                color="rgb(45, 38, 82)"
                placement="start"
            >
                <button
                    onClick={(e: React.MouseEvent) => {
                        if ((e.target as HTMLElement).closest('button') === e.currentTarget)
                            selectTag(tag);
                    }}
                    className={clsx('button-card', 'button-card__tag')}
                >
                    <DragOutlined
                        ref={setActivatorNodeRef}
                        style={{ cursor: 'move', fontSize: '1.25rem' }}
                        {...listeners}
                    />
                    <Flex
                        vertical={
                            (breakpoint.sm && !breakpoint.md) || (breakpoint.xl && !breakpoint.xxl)
                        }
                        gap={16}
                        align={'center'}
                    >
                        <TagIcon
                            name={tag.icon}
                            size={45}
                        />
                        {tag.ru.title}
                    </Flex>
                    <Controls
                        noLabel
                        items={getMenuItems<Tag>(
                            tag,
                            showUpdateForm,
                            showDeleteForm,
                            roles,
                            privileges,
                            'PRIV_CATEGORY_EDIT',
                            'PRIV_CATEGORY_DELETE'
                        )}
                    />
                </button>
            </Badge.Ribbon>
        </Col>
    );
};

const CategoryView = memo(
    ({
        tags,
        selectTag,
        showUpdateForm,
        showCreateForm,
        showDeleteForm,
        handleReorder,
        articlesByTag,
        roles,
        privileges,
        goBack,
    }: CategoryViewProps) => {
        const sensors = useSensors(
            useSensor(MouseSensor, {
                activationConstraint: {
                    distance: 5,
                },
            }),
            useSensor(TouchSensor, {
                activationConstraint: {
                    distance: 5,
                },
            }),
            useSensor(KeyboardSensor, {
                coordinateGetter: sortableKeyboardCoordinates,
            })
        );

        const breakpoint = Grid.useBreakpoint();

        // const handleDragStart = (event) => {
        //     console.log(event);
        // };

        // const handleDragCancel = (event) => {
        //     console.log(event);
        // };

        const handleDragEnd = (event) => {
            handleReorder(event);
        };

        return (
            <Space
                size={18}
                direction="vertical"
            >
                <Flex justify={breakpoint.md ? 'start' : 'space-between'}>
                    <LeftCircleOutlined
                        onClick={goBack}
                        style={{
                            cursor: 'pointer',
                            fontSize: '2rem',
                            display: breakpoint.md ? 'none' : 'inline-flex',
                        }}
                    />

                    {validatePrivileges(roles, privileges, 'PRIV_RUBRIC_CREATE') && (
                        <Button
                            icon={<PlusCircleOutlined />}
                            onClick={showCreateForm}
                            ghost
                        >
                            Добавить рубрику
                        </Button>
                    )}
                </Flex>
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    // onDragStart={handleDragStart}
                    // onDragCancel={handleDragCancel}
                    onDragStart={() => {
                        document.body.style.overflowY = 'hidden';
                    }}
                    onDragEnd={(event) => {
                        document.body.style.overflowY = 'visible';
                        handleDragEnd(event);
                    }}
                >
                    <SortableContext
                        items={tags.map((item) => item.id)}
                        strategy={rectSortingStrategy}
                    >
                        <Row gutter={[20, 18]}>
                            {[...articlesByTag.keys()].map((tagId) => {
                                const tag = tags.find((tag) => tag.id === tagId);
                                const articlesCount = articlesByTag.get(tagId).length;
                                if (!tag) {
                                    return null;
                                }
                                return (
                                    <SortableTag
                                        key={tag.id}
                                        tag={tag}
                                        articlesCount={articlesCount}
                                        selectTag={selectTag}
                                        showUpdateForm={showUpdateForm}
                                        showDeleteForm={showDeleteForm}
                                        roles={roles}
                                        privileges={privileges}
                                    />
                                );
                            })}
                        </Row>
                    </SortableContext>
                </DndContext>
            </Space>
        );
    }
);

export default CategoryView;
